import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useSearchParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Calendar, Clock, Users, MapPin, 
  Dumbbell, Apple, Brain, Video, Check, 
  ExternalLink, User, Info, X, CreditCard, Loader2, IndianRupee, AlertTriangle, Shield
} from "lucide-react";
import { format } from "date-fns";

// Helper to resolve image URLs (handles both external URLs and uploaded files)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api')) {
    return `${API.replace('/api', '')}${url}`;
  }
  return url;
};

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Events = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [registeredEvents, setRegisteredEvents] = useState(new Set());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    fetchEvents();
    // Preload Razorpay script
    loadRazorpayScript();
  }, [category]);

  // Handle auto-opening event from query parameter (from Dashboard)
  useEffect(() => {
    const selectedEventId = searchParams.get('selected');
    if (selectedEventId && events.length > 0) {
      const event = events.find(e => e.event_id === selectedEventId);
      if (event) {
        setSelectedEvent(event);
        setDetailsOpen(true);
      }
    }
  }, [searchParams, events]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = category ? `?category=${category}` : '';
      const response = await axios.get(`${API}/events${params}`, { withCredentials: true });
      setEvents(response.data);
      
      // Use is_registered from the events response directly
      const registrations = new Set();
      for (const event of response.data) {
        if (event.is_registered) {
          registrations.add(event.event_id);
        }
      }
      setRegisteredEvents(registrations);
    } catch (error) {
      console.error("Fetch events error:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (eventId) => {
    const event = events.find(e => e.event_id === eventId);
    if (!event) return;
    
    try {
      const response = await axios.post(`${API}/events/${eventId}/register`, {}, { withCredentials: true });
      
      if (response.data.requires_payment) {
        // Paid event - initiate payment
        await initiatePayment(eventId);
      } else {
        // Free event - registered directly
        toast.success("Registered for event! Check your email for confirmation.");
        setRegisteredEvents(prev => new Set([...prev, eventId]));
        fetchEvents();
      }
    } catch (error) {
      if (error.response?.data?.detail === "Already registered") {
        toast.info("You're already registered");
        setRegisteredEvents(prev => new Set([...prev, eventId]));
      } else {
        toast.error(error.response?.data?.detail || "Failed to register");
      }
    }
  };

  const initiatePayment = useCallback(async (eventId) => {
    setPaymentLoading(true);
    
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      toast.error("Failed to load payment gateway. Please refresh and try again.");
      setPaymentLoading(false);
      return;
    }
    
    try {
      // Create payment order
      const orderResponse = await axios.post(`${API}/events/${eventId}/create-payment`, {}, { withCredentials: true });
      const event = events.find(e => e.event_id === eventId);
      
      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: "WOMBS",
        description: `Registration for ${event?.title}`,
        order_id: orderResponse.data.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/events/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              event_id: eventId
            }, { withCredentials: true });
            
            toast.success("Payment successful! You're registered. Check your email for confirmation.");
            setRegisteredEvents(prev => new Set([...prev, eventId]));
            setDetailsOpen(false);
            fetchEvents();
          } catch (error) {
            toast.error("Payment verification failed. Please contact support.");
          }
          setPaymentLoading(false);
        },
        prefill: {
          name: user?.name,
          email: user?.email,
        },
        theme: {
          color: "#7c9082"
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled");
            setPaymentLoading(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
      
    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setPaymentLoading(false);
    }
  }, [events, user]);

  const handleCancelRegistration = async (eventId) => {
    setCancelLoading(true);
    try {
      await axios.delete(`${API}/events/${eventId}/unregister`, { withCredentials: true });
      setRegisteredEvents(prev => {
        const updated = new Set(prev);
        updated.delete(eventId);
        return updated;
      });
      // Update event attendee count
      setEvents(prev => prev.map(e => 
        e.event_id === eventId 
          ? { ...e, attendee_count: Math.max(0, (e.attendee_count || 1) - 1) }
          : e
      ));
      if (selectedEvent?.event_id === eventId) {
        setSelectedEvent(prev => prev ? { ...prev, attendee_count: Math.max(0, (prev.attendee_count || 1) - 1) } : prev);
      }
      toast.success("Registration cancelled. You will receive a confirmation email.");
      setShowCancelConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel registration");
    } finally {
      setCancelLoading(false);
    }
  };

  const openEventDetails = (event) => {
    setSelectedEvent(event);
    setDetailsOpen(true);
    setShowCancelConfirm(false);
  };

  const getCategoryIcon = (cat) => {
    switch (cat) {
      case 'fitness': return <Dumbbell className="w-4 h-4" />;
      case 'nutrition': return <Apple className="w-4 h-4" />;
      case 'mental_health': return <Brain className="w-4 h-4" />;
      default: return <Video className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'fitness': return 'bg-blue-100 text-blue-600 border-blue-200';
      case 'nutrition': return 'bg-green-100 text-green-600 border-green-200';
      case 'mental_health': return 'bg-purple-100 text-purple-600 border-purple-200';
      default: return 'bg-sage/20 text-sage-dark border-sage/30';
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title="Events & Sessions" />

      {/* Category Filter */}
      <div className="px-6 mt-4 mb-6">
        <div className="max-w-4xl mx-auto flex gap-2 overflow-x-auto pb-2">
          <Button 
            variant={category === null ? "default" : "outline"}
            size="sm"
            onClick={() => setCategory(null)}
            className={`rounded-full ${category === null ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-all"
          >
            All Events
          </Button>
          <Button 
            variant={category === 'fitness' ? "default" : "outline"}
            size="sm"
            onClick={() => setCategory('fitness')}
            className={`rounded-full ${category === 'fitness' ? 'bg-blue-500 text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-fitness"
          >
            <Dumbbell className="w-4 h-4 mr-1" />
            Fitness
          </Button>
          <Button 
            variant={category === 'nutrition' ? "default" : "outline"}
            size="sm"
            onClick={() => setCategory('nutrition')}
            className={`rounded-full ${category === 'nutrition' ? 'bg-green-500 text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-nutrition"
          >
            <Apple className="w-4 h-4 mr-1" />
            Nutrition
          </Button>
          <Button 
            variant={category === 'mental_health' ? "default" : "outline"}
            size="sm"
            onClick={() => setCategory('mental_health')}
            className={`rounded-full ${category === 'mental_health' ? 'bg-purple-500 text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-mental-health"
          >
            <Brain className="w-4 h-4 mr-1" />
            Mental Health
          </Button>
        </div>
      </div>

      {/* Events List */}
      <main className="px-6" data-testid="events-main">
        <div className="max-w-4xl mx-auto space-y-4">
          {loading ? (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i} className="bg-white border-sage/20 animate-pulse">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="w-20 h-20 bg-sage/10 rounded-xl" />
                      <div className="flex-1 space-y-2">
                        <div className="h-6 bg-sage/10 rounded w-3/4" />
                        <div className="h-4 bg-sage/10 rounded w-1/2" />
                        <div className="h-4 bg-sage/10 rounded w-1/4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : events.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-sage/40 mx-auto mb-4" />
              <h3 className="text-xl font-serif text-sage-darker mb-2">No upcoming events</h3>
              <p className="text-muted-foreground">Check back later for new sessions</p>
            </div>
          ) : (
            events.map((event) => {
              const eventDate = new Date(event.scheduled_at);
              const isRegistered = registeredEvents.has(event.event_id);
              
              return (
                <Card 
                  key={event.event_id} 
                  className="bg-white border-sage/20 hover:shadow-lg transition-shadow overflow-hidden cursor-pointer"
                  data-testid={`event-${event.event_id}`}
                  onClick={() => openEventDetails(event)}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Event Image */}
                      <div className="relative w-full md:w-48 h-40 md:h-auto flex-shrink-0">
                        <img 
                          src={resolveImageUrl(event.image_url) || "https://images.unsplash.com/photo-1554042967-bb63c815a74c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white md:block hidden" />
                      </div>
                      
                      {/* Event Info */}
                      <div className="flex-1 p-5 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-2">
                            <Badge variant="outline" className={getCategoryColor(event.category)}>
                              {getCategoryIcon(event.category)}
                              <span className="ml-1 capitalize">{event.category.replace('_', ' ')}</span>
                            </Badge>
                            <h3 className="text-lg font-serif text-sage-darker">{event.title}</h3>
                          </div>
                          
                          {/* Date Card */}
                          <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-sage/20 flex flex-col items-center justify-center">
                            <p className="text-sage-dark font-bold text-xl">{format(eventDate, 'd')}</p>
                            <p className="text-sage/70 text-xs uppercase">{format(eventDate, 'MMM')}</p>
                          </div>
                        </div>
                        
                        <p className="text-muted-foreground text-sm line-clamp-2">{event.description}</p>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {format(eventDate, 'h:mm a')} • {event.duration_minutes} min
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {event.total_slots 
                              ? `${event.attendee_count}/${event.total_slots} slots filled`
                              : `${event.attendee_count} attending`
                            }
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {event.host_name}
                          </div>
                          {event.price_inr > 0 && (
                            <div className="flex items-center gap-1 text-sage-dark font-medium">
                              <IndianRupee className="w-4 h-4" />
                              {event.price_inr.toLocaleString('en-IN')}
                            </div>
                          )}
                          {event.price_inr === 0 && (
                            <Badge variant="outline" className="border-green-300 text-green-600">Free</Badge>
                          )}
                          {event.total_slots && event.attendee_count >= event.total_slots && (
                            <Badge variant="outline" className="border-red-300 text-red-600">Fully Booked</Badge>
                          )}
                        </div>
                        
                        <div className="pt-2 flex items-center gap-2">
                          {isRegistered ? (
                            <Button 
                              variant="outline"
                              size="sm"
                              className="border-sage/30 text-sage-dark hover:bg-sage/10 rounded-full"
                              disabled
                              data-testid={`registered-${event.event_id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Registered
                            </Button>
                          ) : (
                            <Button 
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleRegister(event.event_id); }}
                              className="bg-sage text-white hover:bg-sage-dark rounded-full"
                              disabled={paymentLoading}
                              data-testid={`register-${event.event_id}`}
                            >
                              {paymentLoading ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : event.price_inr > 0 ? (
                                <>
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  Pay ₹{event.price_inr.toLocaleString('en-IN')}
                                </>
                              ) : (
                                "Register"
                              )}
                            </Button>
                          )}
                          <Button 
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openEventDetails(event); }}
                            className="text-muted-foreground hover:text-sage-dark"
                          >
                            <Info className="w-4 h-4 mr-1" />
                            Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>

      {/* Event Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-white border-sage/20 max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedEvent && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <Badge variant="outline" className={getCategoryColor(selectedEvent.category)}>
                    {getCategoryIcon(selectedEvent.category)}
                    <span className="ml-1 capitalize">{selectedEvent.category.replace('_', ' ')}</span>
                  </Badge>
                </div>
                <DialogTitle className="text-sage-darker font-serif text-xl mt-2">
                  {selectedEvent.title}
                </DialogTitle>
              </DialogHeader>
              
              {/* Event Image */}
              <div className="relative h-48 rounded-xl overflow-hidden -mx-2">
                <img 
                  src={resolveImageUrl(selectedEvent.image_url) || "https://images.unsplash.com/photo-1554042967-bb63c815a74c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"}
                  alt={selectedEvent.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="space-y-4 mt-4">
                {/* About */}
                <div>
                  <h4 className="text-sm font-medium text-sage-darker mb-2">About this event</h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">{selectedEvent.description}</p>
                </div>
                
                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-sage/5 rounded-xl">
                    <div className="flex items-center gap-2 text-sage-dark mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-medium">Date & Time</span>
                    </div>
                    <p className="text-sage-darker text-sm">
                      {format(new Date(selectedEvent.scheduled_at), 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {format(new Date(selectedEvent.scheduled_at), 'h:mm a')}
                    </p>
                  </div>
                  
                  <div className="p-3 bg-sage/5 rounded-xl">
                    <div className="flex items-center gap-2 text-sage-dark mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">Duration</span>
                    </div>
                    <p className="text-sage-darker text-sm">{selectedEvent.duration_minutes} minutes</p>
                  </div>
                  
                  <div className="p-3 bg-sage/5 rounded-xl">
                    <div className="flex items-center gap-2 text-sage-dark mb-1">
                      <User className="w-4 h-4" />
                      <span className="text-sm font-medium">Host</span>
                    </div>
                    <p className="text-sage-darker text-sm">{selectedEvent.host_name}</p>
                  </div>
                  
                  <div className="p-3 bg-sage/5 rounded-xl">
                    <div className="flex items-center gap-2 text-sage-dark mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-sm font-medium">Slots</span>
                    </div>
                    {selectedEvent.total_slots ? (
                      <>
                        <p className="text-sage-darker text-sm">
                          {selectedEvent.total_slots - selectedEvent.attendee_count} of {selectedEvent.total_slots} slots available
                        </p>
                        {selectedEvent.attendee_count >= selectedEvent.total_slots && (
                          <p className="text-red-500 text-xs font-medium mt-1">Event is fully booked</p>
                        )}
                      </>
                    ) : (
                      <p className="text-sage-darker text-sm">{selectedEvent.attendee_count} registered (Unlimited slots)</p>
                    )}
                  </div>
                </div>
                
                {/* Location */}
                <div className="p-3 bg-sage/5 rounded-xl">
                  <div className="flex items-center gap-2 text-sage-dark mb-1">
                    <Video className="w-4 h-4" />
                    <span className="text-sm font-medium">Location</span>
                  </div>
                  <p className="text-sage-darker text-sm">{selectedEvent.location || "Online (Video Call)"}</p>
                  <p className="text-muted-foreground text-xs mt-1">Link will be shared after registration</p>
                </div>
                
                {/* Price */}
                {selectedEvent.price_inr > 0 && (
                  <div className="p-4 bg-sage/10 rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="text-sage-darker font-medium">Registration Fee</span>
                      <span className="text-xl font-serif text-sage-darker">
                        ₹{selectedEvent.price_inr.toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                )}
                
                {selectedEvent.price_inr === 0 && (
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700 border-green-300">Free Event</Badge>
                      <span className="text-green-700 text-sm">No registration fee required</span>
                    </div>
                  </div>
                )}

                {/* Non-refundable Notice */}
                {selectedEvent.price_inr > 0 && !registeredEvents.has(selectedEvent.event_id) && (
                  <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-amber-700">
                        <p className="font-semibold mb-1">Non-Refundable Payment</p>
                        <p>All event registrations are final. Payments cannot be refunded once processed. Please ensure you can attend before registering.</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Register/Cancel Button */}
                <div className="pt-2 space-y-3">
                  {registeredEvents.has(selectedEvent.event_id) ? (
                    <>
                      {!showCancelConfirm ? (
                        <div className="space-y-2">
                          <Button 
                            className="w-full bg-sage/20 text-sage-dark rounded-full"
                            disabled
                          >
                            <Check className="w-4 h-4 mr-2" />
                            You&apos;re Registered
                          </Button>
                          <Button 
                            variant="outline"
                            className="w-full border-red-200 text-red-500 hover:bg-red-50 rounded-full"
                            onClick={() => setShowCancelConfirm(true)}
                          >
                            Cancel Registration
                          </Button>
                        </div>
                      ) : (
                        <div className="p-4 bg-red-50 rounded-xl border border-red-200 space-y-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                            <div>
                              <p className="font-semibold text-red-700">Cancel Registration?</p>
                              <p className="text-sm text-red-600 mt-1">
                                {selectedEvent.price_inr > 0 
                                  ? "Your payment will NOT be refunded. This action cannot be undone."
                                  : "You will lose your spot in this event. This action cannot be undone."}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline"
                              className="flex-1 rounded-full"
                              onClick={() => setShowCancelConfirm(false)}
                            >
                              Keep Registration
                            </Button>
                            <Button 
                              className="flex-1 bg-red-500 text-white hover:bg-red-600 rounded-full"
                              onClick={() => handleCancelRegistration(selectedEvent.event_id)}
                              disabled={cancelLoading}
                            >
                              {cancelLoading ? "Cancelling..." : "Yes, Cancel"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : selectedEvent.total_slots && selectedEvent.attendee_count >= selectedEvent.total_slots ? (
                    <Button 
                      className="w-full bg-gray-200 text-gray-500 rounded-full"
                      disabled
                    >
                      Event is Fully Booked
                    </Button>
                  ) : (
                    <Button 
                      className="w-full bg-sage text-white hover:bg-sage-dark rounded-full"
                      onClick={() => handleRegister(selectedEvent.event_id)}
                      disabled={paymentLoading}
                    >
                      {paymentLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : selectedEvent.price_inr > 0 ? (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Pay ₹{selectedEvent.price_inr.toLocaleString('en-IN')} & Register
                        </>
                      ) : (
                        "Register for this Event"
                      )}
                    </Button>
                  )}
                </div>

                {/* Legal Footer */}
                <div className="pt-3 border-t border-sage/10">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Shield className="w-3 h-3" />
                    <span>By registering, you agree to our <a href="/terms" className="text-sage-dark underline">Terms of Service</a> and <a href="/privacy" className="text-sage-dark underline">Privacy Policy</a>. Your data is securely processed.</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;
